import React, { useState } from 'react';
import axios from 'axios';
import { ArrowLeftIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import Notify from '../../../utils/useNotify';
import { useNavigate } from 'react-router';

export default function CreateCarousel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const localhost = process.env.REACT_APP_LOCALHOST;
  const [title, setTitle] = useState({ en: '', fr: '' });
  const [details, setDetails] = useState({ en: '', fr: '' });
  const [image, setImage] = useState(null);
  const [imageName, setImageName] = useState('');
  const [imageError, setImageError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('title', JSON.stringify(title));
    formData.append('details', JSON.stringify(details));
    if (image) {
      formData.append('image', image);
    }

    try {
      await axios.post(localhost + 'store-carousel', formData, { headers: { Authorization: `Bearer ${token}` } });
      Notify("success", t("create-slide-success"));
      setTimeout(() => navigate("/admin/slides"), 1000);
    } catch (error) {
      Notify("error", t("create-slide-error"));
    }
  };

  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();

    reader.onloadend = () => {
      const img = new Image();
      img.src = reader.result;
      img.onload = () => {
        setImage(file);
        setImageName(file.name);
        setImageError('');
      };
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    accept: 'image/*'
  });

  return (
    <div className="antialiased">
      <div className="max-w-7xl mx-auto">
        <div className="w-full flex justify-start mb-2">
          <a href="/admin/slides" className="flex gap-1 items-center text-sm bg-black hover:bg-black/80 text-white pl-2 pr-3 py-2 rounded-md">
            <ArrowLeftIcon className="size-4" /> {t("back")}
          </a>
        </div>
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
          <form onSubmit={handleSubmit} className="px-4 py-6 sm:p-8">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">{t("crs-create-slide")}</h2>
                <p className="mt-1 text-sm text-gray-600">{t("crs-slide-description")}</p>
              </div>
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-700">Title <span className="text-orange-500 text-xs">({t("english")})</span></label>
                  <input name="titleen" type="text" value={title.en} onChange={(e) => setTitle({ ...title, en: e.target.value })}
                    className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300" required />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Titre <span className="text-orange-500 text-xs">({t("french")})</span></label>
                  <input name="titlefr" type="text" value={title.fr} onChange={(e) => setTitle({ ...title, fr: e.target.value })}
                    className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300" required />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Description <span className='text-orange-500 text-xs'>({t("english")})</span></label>
                  <textarea value={details.en} onChange={(e) => setDetails({ ...details, en: e.target.value })}
                    rows={3} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300" required />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Description <span className='text-orange-500 text-xs'>({t("french")})</span></label>
                  <textarea value={details.fr} onChange={(e) => setDetails({ ...details, fr: e.target.value })}
                    rows={3} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300" required />
                </div>
                <div className="sm:col-span-2">
                  <div {...getRootProps()} className="w-full py-9 bg-gray-50 rounded-2xl border border-gray-300 gap-3 grid border-dashed">
                    <div className="grid gap-1">
                      <PhotoIcon className="size-10 mx-auto mb-5" />
                      <h2 className="text-center text-gray-400 text-xs leading-4">
                        {t("carousel-image-area-note-1")} <br />
                        {t("carousel-image-area-note-2")}
                        </h2>
                    </div>
                    <div className="grid gap-2">
                      <h4 className="text-center text-gray-900 text-sm font-medium leading-snug">{t("crs-drop-and-drag")}</h4>
                      <div className="flex items-center justify-center">
                        <label>
                          <input {...getInputProps()} type="file" name="image" className="opacity-0" />
                          <div className="flex w-max h-9 px-3 flex-col bg-black hover:bg-black/80 rounded-full shadow text-white text-xs font-semibold leading-4 items-center justify-center cursor-pointer focus:outline-none">
                          {t("crs-choose-file")}
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                  {imageError && <p className="text-red-500 text-center text-sm mt-3">{imageError}</p>}
                  {imageName && !imageError && (
                    <p className="text-center text-gray-700 text-sm mt-3">{t("file-name")}: {imageName}</p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-x-6">
                <button type="submit" className="rounded-md bg-black hover:bg-black/80 px-6 py-2 text-sm font-semibold text-white">
                  Create Slide
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
